import { FormProvider, useForm } from 'react-hook-form';
import {
  establishmentDetailsDtoSchema,
  initialEstablishmentDetailsDto,
} from './form-config';
import { useEffect, useMemo, useState } from 'react';
import {
  useGetEnterpriseById,
  useGetEnterprisesSuggestions,
} from '@enterprise/queries/useGetEnterprises';

import CreditCodesEnum from '@settings/models/credits-codes.enum';
import { EstablishmentBlockStyled } from '../establishment-block.styled';
import { EstablishmentDetailsFormDto } from '@establishment/models/establishment-form';
import EstablishmentFormFooter from '../EstablishmentFormFooter';
import IOption from '@models/option';
import { JAutocomplete } from '@components/molucules/JAutocomplete';
import JNumberField from '@atoms/JNumberField';
import { JPhoneInput } from '@atoms/JPhoneInput';
import JTextFieldForm from '@atoms/JTextFieldForm';
import Jpaper from '@atoms/JPaper';
import { Loading } from '@atoms/Loading';
import { Stack } from '@mui/material';
import { useDebouncedCallback } from 'use-debounce';
import useEstablishmentMutation from '@establishment/hooks/useEstablishmentMutation';
import { useGetCreditsSettings } from '@settings/queries/useGetSettings';
import { useGetRomeDomainsPaged } from '@/queries/useGetRomeDomain';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

interface EstablishmentFormDetailsProps {
  includeFooter?: boolean;
}
const EstablishmentFormDetails = ({
  includeFooter,
}: EstablishmentFormDetailsProps) => {
  const [sectorInputValue, setSectorInputValue] = useState<string>('');
  const [enterpriseInputValue, setEnterpriseInputValue] = useState<string>('');

  const {
    setStepIndex,
    payload,
    setSectors,
    sectors,
    setEnterprise,
    enterprise,
    setIsDirty,
    mode,
    setPayload,
  } = useEstablishmentMutation();
  const { data: romeDomains } = useGetRomeDomainsPaged(sectorInputValue);
  const { data: enterprises } = useGetEnterprisesSuggestions(
    10,
    1,
    enterpriseInputValue,
  );

  const { t } = useTranslation();

  const { data: dataSettings } = useGetCreditsSettings();

  const methods = useForm<EstablishmentDetailsFormDto>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: useMemo(
      () => initialEstablishmentDetailsDto,
      [initialEstablishmentDetailsDto],
    ),
    resolver: yupResolver(establishmentDetailsDtoSchema(t)),
  });
  const { handleSubmit, setValue, trigger, formState, getValues, watch } =
    methods;
  const { isDirty, isValid } = formState;

  const { data: enterpriseData, isSuccess: fetchEnterpriseDataSuccess } =
    useGetEnterpriseById(
      getValues('enterpriseId'),
      !!getValues('enterpriseId'),
    );

  const onSubmit = handleSubmit((data) => {
    setStepIndex((prev) => (prev ? prev + 1 : 1));
  });

  const updateSectors = (options: IOption[], shouldDirty: boolean): void => {
    setSectors(options);
    setValue(
      'activityAreaIds',
      options.map((item) => item.value),
      { shouldDirty },
    );
    trigger('activityAreaIds');
  };

  useEffect(() => {
    if (!sectors || sectors?.length === 0) return;
    setValue(
      'activityAreaIds',
      sectors.map((item) => item.value),
      { shouldDirty: true },
    );
    trigger('activityAreaIds');
  }, []);

  const debouncedSector = useDebouncedCallback((value) => {
    setSectorInputValue(value);
  }, 1000);
  const debouncedEnterprise = useDebouncedCallback((value) => {
    setEnterpriseInputValue(value);
  }, 1000);
  const enterprisesOptions = enterprises
    ? enterprises.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];
  const romeDomainOptions = romeDomains
    ? romeDomains.data.data.map(({ id, name }) => {
        return { value: id, label: name };
      })
    : [];

  useEffect(() => {
    if (dataSettings?.data) {
      const settingsDefaultThreshold = dataSettings?.data.find(
        ({ code }) => code === CreditCodesEnum.DefaultThreshold,
      )?.value;
      if (settingsDefaultThreshold && !getValues('creditsAlertThreshold')) {
        setValue('creditsAlertThreshold', settingsDefaultThreshold);
        trigger('creditsAlertThreshold');
      }
    }
  }, [dataSettings]);

  useEffect(() => {
    if (isDirty) return;
    // Check if payload is available and update default values accordingly
    if (payload?.establishmentDto) {
      setValue('name', payload.establishmentDto.name);
      setValue('siret', payload.establishmentDto.siret);
      setValue('enterpriseId', payload.establishmentDto.enterpriseId);
      setValue('email', payload.establishmentDto.email);
      setValue('activityAreaIds', payload.establishmentDto.activityAreaIds);
      setValue('phone', payload.establishmentDto.phone);
      setValue('mobilePhone', payload.establishmentDto.mobilePhone);
      setValue(
        'creditsAlertThreshold',
        payload.establishmentDto.creditsAlertThreshold,
      );

      trigger();
    }
  }, [payload, setValue]);

  useEffect(() => {
    if (!fetchEnterpriseDataSuccess) return;
    setEnterprise(enterpriseData.data);
  }, [fetchEnterpriseDataSuccess]);

  useEffect(() => {
    if (mode === 'edit') return;
    const defaultEstablishmentOptions = enterpriseData?.data.activityAreas
      .filter((item) => item !== undefined)
      .map((item) => ({
        label: item.romeDomain.name,
        value: item.romeDomain.id,
      }));
    if (defaultEstablishmentOptions)
      updateSectors(defaultEstablishmentOptions, false);
  }, [enterpriseData, fetchEnterpriseDataSuccess, mode]);

  useEffect(() => {
    return () => {
      const values = getValues();
      setPayload((prev) => {
        return {
          addressDto: prev?.addressDto,
          establishmentDto: {
            activityAreaIds: values.activityAreaIds,
            name: values.name,
            siret: values.siret,
            enterpriseId: values.enterpriseId,
            email: values.email,
            mobilePhone: values.mobilePhone,
            phone: values.phone,
            creditsAlertThreshold: values.creditsAlertThreshold,
          },
          video: prev?.video,
          thumbnail: prev?.thumbnail,
          thumbnailUrl: prev?.thumbnailUrl,
          videoUrl: prev?.videoUrl,
          id: prev?.id,
        };
      });
    };
  }, [getValues, setPayload]);

  useEffect(() => {
    setIsDirty(isDirty && isValid);
  }, [isDirty, isValid]);

  useEffect(() => {
    if (!isDirty || !payload) return;

    setPayload((prev) => {
      return {
        ...prev,
        establishmentDto: {
          ...getValues(),
        },
      };
    });
  }, [
    setPayload,
    getValues,
    watch('activityAreaIds'),
    watch('name'),
    watch('siret'),
    watch('enterpriseId'),
    watch('email'),
    watch('mobilePhone'),
    watch('phone'),
    watch('creditsAlertThreshold'),
  ]);

  if (!enterprise?.name && mode === 'edit') return <Loading />;
  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} autoComplete="off" noValidate>
        <Stack gap={2}>
          <EstablishmentBlockStyled>
            <Jpaper gap={1} alignItems="stretch" attr={{ fullHeight: true }}>
              <JAutocomplete
                defaultValue={{
                  label: enterprise?.name ?? '',
                  value: enterprise?.id ?? '',
                }}
                required
                fullWidth
                label={t('NAME_ENTERPRISE')}
                options={enterprisesOptions}
                onChange={(ev, newValue) => {
                  setValue('enterpriseId', (newValue as IOption)?.value, {
                    shouldDirty: true,
                  });
                  trigger('enterpriseId');
                }}
                onTextChange={(val) => debouncedEnterprise(val)}
              />

              <JAutocomplete
                value={sectors ?? []}
                multiple
                required
                fullWidth
                label={t('SECTOR')}
                options={romeDomainOptions}
                onChange={(ev, newValue) =>
                  updateSectors(
                    newValue as { value: string; label: string }[],
                    true,
                  )
                }
                onTextChange={(val) => debouncedSector(val)}
              />

              <JTextFieldForm
                required
                name="name"
                fullWidth
                noBorder
                label={t('NAME_ESTABLISHMENT')}
              />

              <JTextFieldForm
                required
                name="siret"
                fullWidth
                noBorder
                label={t('SIRET')}
              />
              <JTextFieldForm
                required
                name="email"
                fullWidth
                noBorder
                label={t('EMAIL')}
              />

              <JPhoneInput
                required
                value={payload?.establishmentDto?.phone}
                specialLabel={t('PHONE_NUMBER') ?? ''}
                onChange={(value) => {
                  if (value !== '')
                    setValue('phone', '+' + value, { shouldDirty: true });
                  else setValue('phone', null, { shouldDirty: true });
                  trigger('phone');
                }}
              />
              <JPhoneInput
                value={payload?.establishmentDto?.mobilePhone}
                specialLabel={t('PHONE_PRO') ?? ''}
                onChange={(value) => {
                  if (value !== '')
                    setValue('mobilePhone', '+' + value, { shouldDirty: true });
                  else setValue('mobilePhone', null, { shouldDirty: true });
                  trigger('mobilePhone');
                }}
              />
              <JNumberField
                size="medium"
                name={'credit-alert-threshold'}
                defaultValue={getValues('creditsAlertThreshold')}
                onNumberChange={(value) => {
                  setValue('creditsAlertThreshold', value.toString(), {
                    shouldDirty: true,
                  });
                  trigger('creditsAlertThreshold');
                }}
                label={t('DEFAULT_THRESHOLD')}
                fullWidth
              />
            </Jpaper>
          </EstablishmentBlockStyled>
          {includeFooter && (
            <EstablishmentFormFooter isValid={isValid} values={getValues()} />
          )}
        </Stack>
      </form>
    </FormProvider>
  );
};

export default EstablishmentFormDetails;
